import React, { useEffect, useRef, useState } from "react";
import { Cropper } from "react-cropper";
import type { ReactCropperElement, ReactCropperProps } from "react-cropper";
import "cropperjs/dist/cropper.css";
import { Button } from "Atoms";

/* Crop dialog body, rendered by AttachmentManager's IMAGE CROPPER modal.
 * Flow: load the source image into an object URL → user adjusts the crop box
 * (live preview on the right) → Save turns the cropped canvas into a PNG
 * `File` and hands it to onSave. What happens to that File afterwards is
 * AttachmentManager's job (see its `CROP-NOTE` comments). */
interface ImageCropperProps {
  fileOrUrl: File | string; // signed URL (remote attachment) or local File
  isOpen: boolean;
  onCancel: () => void;
  onSave: (croppedFile: File) => void; // receives the cropped PNG File
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  fileOrUrl,
  isOpen,
  onCancel,
  onSave,
}) => {
  // Handle to the underlying cropperjs instance (getCroppedCanvas etc.).
  const cropperRef = useRef<ReactCropperElement>(null);

  // `src` for <Cropper>. Should be an object URL once the effect below runs.
  // CROP-NOTE: initialised with the raw prop, so on the first render it can
  // briefly be a File object rather than a URL string.
  const [imageURL, setImageURL] = useState(fileOrUrl);

  // Name given to the cropped File: "cropped_" + original name.
  const [fileName, setFileName] = useState("cropped_image.png");

  // Data URL of the current crop, shown in the "Cropped Image" preview.
  const [previewURL, setPreviewURL] = useState<string | null>(null);

// Turn the source into an object URL the cropper can load. Remote images are
// fetched into a Blob first (presumably so the canvas isn't CORS-tainted and
// can still be exported in handleSave).
// CROP-NOTE: the `return () => URL.revokeObjectURL(...)` lines are inside the
// async loadImageAsBlob, not returned from the effect itself, so React never
// runs them — object URLs are never revoked.
useEffect(() => {
  const loadImageAsBlob = async () => {
    if (typeof fileOrUrl === "string") {
      try {
        const response = await fetch(fileOrUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setImageURL(objectUrl);
        // CROP-NOTE: last path segment of a *signed* URL still carries its
        // query string, e.g. "cropped_photo.jpg?X-Amz-Signature=…", which
        // ends up as the uploaded filename.
        const urlParts = fileOrUrl.split("/");
        setFileName(`cropped_${urlParts[urlParts.length - 1]}`);

        return () => URL.revokeObjectURL(objectUrl);
      } catch (err) {
        console.error("Failed to load image blob from signed URL", err);
      }
    } else {
      const objectUrl = URL.createObjectURL(fileOrUrl);
      setImageURL(objectUrl);
      setFileName(`cropped_${fileOrUrl.name}`);
      return () => URL.revokeObjectURL(objectUrl);
    }
  };

  loadImageAsBlob();
}, [fileOrUrl]);


  // Live preview: polls the cropper every 50ms and re-renders the crop as a
  // PNG data URL (simple, but re-encodes the image ~20×/second).
  useEffect(() => {
    const interval = setInterval(() => {
      const cropper = cropperRef.current?.cropper;
      if (!cropper) return;
      const canvas = cropper.getCroppedCanvas();
      if (canvas) {
        setPreviewURL(canvas.toDataURL("image/png"));
      }
    }, 50);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Export the current crop as a File and hand it to the parent.
  const handleSave = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    const canvas = cropper.getCroppedCanvas();

    if (!canvas) return;
    canvas.toBlob(
      (blob: any) => {
        if (blob) {
          // CROP-NOTE: the blob is always PNG (see "image/png" below), but
          // fileName keeps the original extension — a ".jpg" name can end up
          // holding PNG data. (The 0.92 quality arg is ignored for PNG.)
          const croppedFile = new File([blob], fileName, {
            type: blob.type,
            lastModified: Date.now(),
          });
          onSave(croppedFile);
        }
      },
      "image/png",
      0.92
    );
  };

  if (!isOpen) return null;

  return (
    <div className="bg-black/60 flex items-center justify-center">
      <div className="bg-white rounded-lg p-4 w-[90%] max-w-5xl">
        <h2 className="text-lg font-semibold mb-2">Crop Image</h2>
        <div className="flex gap-6">
          <div className="mb-4 w-1/2">
            <Cropper
              {...({
                src: imageURL,
                style: { height: "auto", width: "100%" },
                aspectRatio: NaN, // ✅ Allows free-form cropping (no fixed ratio)
                guides: true,
                viewMode: 1, // Recommended for flexible movement
                autoCropArea: 0.8, // Starts with 80% crop area
                background: false,
                responsive: true,
                movable: true,
                cropBoxResizable: true, // ✅ Ensures user can resize crop box
                cropBoxMovable: true, // ✅ Enables moving the crop box
                dragMode: "crop",
                ref: cropperRef,
              } as ReactCropperProps)}
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <br />
            <Button color="error" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave} sx={{ marginLeft: 2 }}>
              Save
            </Button>
          </div>

          <div className="">
            <h3 className="">Cropped Image</h3>
            {previewURL ? (
              <img
                width={"100%"}
                height={"auto"}
                src={previewURL}
                alt="Preview"
                className="bxShadow"
              />
            ) : (
              <p>No preview available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
